/**
 * 메뉴 : AI 메뉴디자이너 (SFA > 이슈관리 > 메뉴디자이너)
 *
 * 명령 입력은 헤더의 SNotes AI Bot(챗봇)으로 받는다 — studioAiBridge로 연결.
 *
 * - 최초 진입: 랜딩(빈 상태) 화면
 * - 챗봇 명령 → POST /snotes/aiStudio → draft 수신 →
 *   공통 컴포넌트(SearchPanel/SnAgGrid)로 실제 화면과 동일하게 미리보기
 * - 임시저장/배포는 Agent가 Tool로 처리한다. 프론트는 명령만 전달하고,
 *   배포는 브리지가 확인 문답을 거친 뒤 Agent에게 지시한다(studioAiBridge).
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { global } from "@shared/libs/Global";
import { C } from "@shared/libs/designTokens";
import { showDialog } from "@widgets/CommonDialog";
import LandingView from "./components/LandingView";
import EditorView from "./components/EditorView";
import { type StudioModel } from "./studioTypes";
import { type DraftQueries, type StudioSpec } from "./studioAiApi";
import {
  specToPreviewMeta,
  attachQueries,
  type PreviewMeta,
} from "./previewMeta";
import {
  registerStudioAiBridge,
  unregisterStudioAiBridge,
  type StudioAiBridgeContext,
} from "./studioAiBridge";
import "./designer.css";

interface Props {
  Menuprops: menuInfo;
}

type Phase = "landing" | "editor";

const SN_SFA_ISSUE_REG_006 = forwardRef(({ Menuprops }: Props, ref) => {
  const [phase, setPhase] = useState<Phase>("landing");
  const [screen, setScreen] = useState<StudioModel | null>(null);
  // 공통 컴포넌트(SearchPanel/SnAgGrid)에 넣을 미리보기 메타 (draft에서 변환)
  const [previewMeta, setPreviewMeta] = useState<PreviewMeta | null>(null);

  // 이슈 상세(DESIGN 버튼)에서 진입 시 nodeData로 끌고 온 이슈 컨텍스트.
  // no_issue → noIssue (저장·배포 시 새 화면에 스탬프될 cd_menu)
  // 소스 화면은 서버로 따로 보내지 않는다 — 사용자가 명령에 담아 말한다
  // (예: "발주현황 참고해서 이슈등록 화면 만들어줘")
  const issueNo: string | undefined = Menuprops?.nodeData?.no_issue;

  // 담당자사번(cdEmp) — 저장·배포에 필요. 로그인 사용자 사번 우선, 없으면 이슈 담당자로 폴백
  const { userInfo } = global();
  const cdEmp: string | undefined =
    userInfo?.cd_emp || Menuprops?.nodeData?.cd_emp_admin || undefined;

  // AI가 만든 이번 턴의 화면 초안을 편집기에 적용.
  // 조회쿼리(draftQueries)는 draft 밖에서 오므로 메타 변환 후 얹는다.
  const applyScreen = (
    model: StudioModel,
    draft: StudioSpec,
    draftQueries: DraftQueries
  ) => {
    setScreen(model);
    setPreviewMeta(attachQueries(specToPreviewMeta(draft), draftQueries));
    setPhase("editor");
  };

  // 상단 공통 툴바(돋보기 등) → 편집기로 라우팅
  const editorRef = useRef<any>(null);

  // SNotes AI Bot(챗봇) 명령을 받기 위한 브리지 등록 (탭 마운트 동안 유지)
  useEffect(() => {
    const ctx: StudioAiBridgeContext = {
      issueNo,
      cdEmp,
      applyScreen,
      // 새 채팅 — 편집 중이던 화면을 버리고 빈 상태로 되돌린다
      onNewChat: () => {
        setScreen(null);
        setPreviewMeta(null);
        setPhase("landing");
      },
    };
    registerStudioAiBridge(ctx);
    return () => unregisterStudioAiBridge(ctx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueNo, cdEmp]);

  useImperativeHandle(
    ref,
    () => ({
      onSearch: () => {
        if (phase === "editor") editorRef.current?.onSearch?.();
      },
      // 저장/삭제는 챗봇이 담당한다. 아무 반응 없이 무시하면 눌린 걸 못 알아채므로 안내한다.
      onSave: () => {
        showDialog(
          "info",
          '화면 저장(배포)은 우측 상단 SNotes AI Bot에 "배포해줘"라고 입력해 주세요.\n' +
            '배포 전 편집 내용만 남겨두려면 "임시저장해줘"라고 입력하시면 됩니다.'
        );
      },
      onDelete: () => {
        showDialog("info", "메뉴디자이너에서는 삭제를 지원하지 않습니다.");
      },
    }),
    [phase]
  );

  return (
    <div
      className="snd-root"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: phase === "editor" ? "#fff" : C.bg,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: 1,
          position: "relative",
          minHeight: 0,
          padding: 0,
        }}
      >
        {phase === "landing" && <LandingView />}
        {phase === "editor" && screen && previewMeta && (
          <EditorView
            ref={editorRef}
            screen={screen}
            meta={previewMeta}
            Menuprops={Menuprops}
          />
        )}
      </div>
    </div>
  );
});

export default SN_SFA_ISSUE_REG_006;
